import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getListingAvailability } from '../../utils/listingAvailability';
import { MapPinOff } from 'lucide-react';
import type { DishListing } from '../../types/listing';
import { formatTimeRange, formatTimeWindow } from '../../utils/timeWindow';
import { formatPriceCents } from '../../utils/currency';
import { useLocationStore } from '../../store/useLocationStore';
import {
  deriveTimezone,
  timeZoneAbbreviation,
  zoneOffsetMinutes,
  zonedWallClockToInstant,
} from '../../utils/timezone';
// Tag display delegates to the shared `displayTag` helper, which handles
// both the new BE wire format ("value,label" — label part is the translated
// display string) and the legacy plain-value format (kebab-case, title-
// cased on the fly). See `src/utils/tagLabel.ts`.
import { displayTag as formatTag } from '../../utils/tagLabel';
import { getFulfillmentIcons, Icons } from '../../services/iconService';
import { UserAttribution } from '../ui/UserAttribution';
import { Modal } from '../ui/Modal';
import { useProfileStore } from '../../store/useProfileStore';

/** Minimal listing shape accepted by DishInfoCard (e.g. from an order summary). */
type MinimalListing = Pick<DishListing, 'title'> &
  Partial<Omit<DishListing, 'title'>>;

type DishInfoCardProps = {
  listing: DishListing | MinimalListing;
  onClick?: () => void;
  className?: string;
  /** Start expanded. Defaults to false. */
  defaultExpanded?: boolean;
  /** Hide the expand chevron entirely (forces collapsed). */
  disableExpand?: boolean;
  /**
   * Visual treatment of the card surface.
   *  - `default`: thin border + small shadow, tuned for theme-aware backgrounds
   *    (home feed list, etc.).
   *  - `floating`: hardcoded drop shadow, no border — for use on top of the
   *    map (which is always a light/neutral background regardless of theme).
   */
  variant?: 'default' | 'floating';
  /**
   * Opt-in to the distance affordance (browse contexts: feed, map, detail).
   * When on: distance shows only if the user's location is device-sourced;
   * otherwise a "no location" pin icon renders that calls `onDistanceGateClick`.
   * Left off for non-browse cards (orders, activity, profile) which carry no
   * distance and shouldn't surface the icon.
   */
  showDistanceGate?: boolean;
  /** Invoked when the user taps the "no location" pin (opens the enable-location explainer). */
  onDistanceGateClick?: () => void;
  /**
   * Opt-in to the sold-out stamp: a light scrim over the dish photo with a
   * translucent watermark naming why it can't be ordered. Scoped like
   * `showDistanceGate` to browse contexts (feed, map, detail) — the places someone is deciding
   * whether to order. Deliberately off for the order and activity cards, where
   * the same card describes a dish the user has ALREADY bought and a "Sold out"
   * stamp would read as something having gone wrong with their order.
   *
   * Covers both sold-out routes (portions ran out, lister closed orders early)
   * but not retired listings, which keep the existing grayscale treatment.
   */
  showSoldOutStamp?: boolean;
  /**
   * The viewer is this listing's chef. Range and location prompts ("Your
   * location required", "Too far to deliver") are eater questions about
   * whether the food can reach them, so the chef's own view states only the
   * drop-off fee.
   */
  ownListing?: boolean;
  /**
   * Drop the drop-off window and its chip entirely when the eater is beyond
   * the radius. For the home feed, where a method they can't use is clutter;
   * the listing page still shows it with the "too far" explainer.
   */
  hideOutOfRangeDropoff?: boolean;
};

export const DishInfoCard: React.FC<DishInfoCardProps> = ({
  listing,
  onClick,
  className,
  defaultExpanded = false,
  disableExpand = false,
  variant = 'default',
  showDistanceGate = false,
  ownListing = false,
  hideOutOfRangeDropoff = false,
  onDistanceGateClick,
  showSoldOutStamp = false,
}) => {
  // 'orders' is declared alongside 'listing' so the `orders:` cross-namespace
  // lookups below actually resolve — without it i18next renders the raw key.
  // 'listing' stays first, so unprefixed keys still resolve against it.
  const { t } = useTranslation(['listing', 'orders']);
  const {
    title,
    description,
    price,
    imageUrl,
    thumbnailUrl,
    itemsRemaining,
    quantity,
    status,
    listerSummary,
    distance,
    dietaryTags,
    allergenTags,
    originTags,
    timeWindow,
    pickupWindow,
    dropoffWindow,
    dropoffRadius,
    dropoffFee,
    fulfillmentMethod,
    dropoffRunStartedAt,
  } = listing;

  // The chef offered drop-off but has already left on the run, so eater
  // payloads no longer list it. Say so rather than letting it vanish: someone
  // who saw drop-off on this dish earlier would otherwise wonder where it went.
  const dropoffClosed =
    !ownListing &&
    !!dropoffRunStartedAt &&
    !!dropoffWindow?.start &&
    !fulfillmentMethod?.includes('dropoff');

  // Same distance comparison as the 'far' chip below. Distance is only present
  // when it came from the device, so an unknown location never hides anything.
  // Only when pickup is still on offer: on a drop-off-only card, hiding the
  // window would leave a bare time with no method, so the chip stays and says
  // "Too far" (the feed sinks those listings to the bottom).
  const hideDropoff =
    hideOutOfRangeDropoff &&
    !ownListing &&
    !!fulfillmentMethod?.includes('pickup') &&
    distance != null &&
    dropoffRadius != null &&
    distance > dropoffRadius;

  const availabilityLabel = formatTimeWindow(timeWindow ?? null);
  const fulfillmentIcons = getFulfillmentIcons(fulfillmentMethod).filter(
    (f) => !(hideDropoff && f.method === 'dropoff'),
  );

  // One pill per hand-off window, earliest first.
  //
  // The card used to show the ENVELOPE (`timeWindow`) as a single label plus a
  // row of method icons somewhere else entirely, which on a pickup+drop-off
  // listing said "6:30 to 9:00pm" — a span the chef is never actually
  // available for as one block — and gave no way to tell which window belonged
  // to which method. Pairing each window with its own icon and colour is the
  // whole point: the times mean nothing without knowing which is which.
  //
  // Falls back to the single envelope pill when the listing has no method
  // windows, which covers everything written before the split (and anything
  // not yet touched by the pickup_window backfill).
  const windowPills = useMemo(() => {
    const entries: { method: 'pickup' | 'dropoff'; window: NonNullable<typeof pickupWindow> }[] = [];
    if (fulfillmentMethod?.includes('pickup') && pickupWindow?.start) {
      entries.push({ method: 'pickup', window: pickupWindow });
    }
    if (fulfillmentMethod?.includes('dropoff') && dropoffWindow?.start && !hideDropoff) {
      entries.push({ method: 'dropoff', window: dropoffWindow });
    }
    entries.sort((a, b) => (a.window!.start < b.window!.start ? -1 : 1));

    return entries.map((e, i) => ({
      method: e.method,
      Icon: e.method === 'pickup' ? Icons.pickup : Icons.dropoff,
      tone: e.method === 'pickup' ? 'secondary' : 'tertiary',
      // Only the first pill carries the day. A later window on the same date
      // repeating "Tonight" is noise; one that lands on a different date needs
      // it, so it gets the full label back.
      label:
        i === 0 || e.window!.start.split('T')[0] !== entries[0].window!.start.split('T')[0]
          ? formatTimeWindow(e.window ?? null)
          : formatTimeRange(e.window ?? null),
      // The expanded rows sit apart from each other, so a row without the day
      // reads as undated. They always carry it.
      fullLabel: formatTimeWindow(e.window ?? null),
    }));
  }, [fulfillmentMethod, pickupWindow, dropoffWindow, hideDropoff]);

  // What the drop-off status chip says, or null when there's nothing to say.
  //
  // Three states, and which one applies turns on whether we know where the
  // eater actually is. `distance` is already nulled upstream unless the
  // location came from the device (see stripDistance in useListings), so a
  // present distance IS the permission to compare against the radius — there's
  // no second gate to keep in sync here.
  //
  //   no distance   → "your location required", tapping opens the same modal
  //                   as the no-location pin next to the miles
  //   beyond radius → "too far to deliver"
  //   within radius → the fee, which is otherwise invisible: the card's price
  //                   says "Free" on a listing that charges $2 to drive it over
  const dropoffStatus = useMemo((): null | {
    kind: 'fee' | 'far' | 'locate';
    /** Never rendered as text — the pill shows a price or an icon. Screen
     *  readers get the sentence, since a bare glyph says nothing out loud. */
    aria: string;
    /** Present only for `fee`: the price, or "Free" rather than "$0.00" when
     *  the chef drives it over for nothing. */
    fee?: string;
  } => {
    if (!fulfillmentMethod?.includes('dropoff')) return null;
    if (ownListing) {
      return dropoffFee
        ? {
            kind: 'fee',
            aria: t('card.dropoffFee', { fee: formatPriceCents(dropoffFee) }),
            fee: formatPriceCents(dropoffFee),
          }
        : { kind: 'fee', aria: t('card.dropoffFree'), fee: t('card.dropoffFreeShort') };
    }
    if (distance == null) {
      // Only offer the location prompt where the card is already gating on it;
      // outside browse contexts there's no modal to open.
      return showDistanceGate
        ? { kind: 'locate', aria: t('card.dropoffNeedsLocation') }
        : null;
    }
    if (dropoffRadius != null && distance > dropoffRadius) {
      return { kind: 'far', aria: t('card.dropoffOutOfRange') };
    }
    return dropoffFee
      ? {
          kind: 'fee',
          aria: t('card.dropoffFee', { fee: formatPriceCents(dropoffFee) }),
          fee: formatPriceCents(dropoffFee),
        }
      : { kind: 'fee', aria: t('card.dropoffFree'), fee: t('card.dropoffFreeShort') };
  }, [fulfillmentMethod, distance, dropoffRadius, dropoffFee, showDistanceGate, ownListing, t]);

  // Expanded has the width the collapsed overlay doesn't, so it spells things
  // out: the method is named rather than implied by a glyph, and the drop-off
  // status is a sentence rather than a price or a struck-through icon. Same
  // `windowPills` data, so the two views can't disagree about the facts — only
  // about how much room they have to state them.
  const renderWindowRows = () => (
    <div className="flex w-full flex-col gap-3">
      {windowPills.map(({ method, Icon, tone, fullLabel: label }) => (
        <div key={method} className="flex items-start gap-2">
          <span
            className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: `var(--ion-color-${tone})`, color: '#000' }}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
          </span>
          {/* Two lines rather than one: at this width a single row broke
              "Drop off" across lines and split the status mid-phrase. The
              method names itself, then the details sit under it and wrap as a
              unit. */}
          <div className="flex min-w-0 flex-col">
            <span className="font-medium text-foreground">
              {t(`orders:card.fulfillment.${method}` as const)}
            </span>
            <span className="text-muted-foreground">
              <span className="whitespace-nowrap">
                {tzAbbrev ? `${label} ${tzAbbrev}` : label}
              </span>
              {method === 'dropoff' && dropoffStatus ? (
                <>
                  <span aria-hidden className="mx-1.5 opacity-40">·</span>
                  {dropoffStatus.kind === 'locate' ? (
                    <button
                      type="button"
                      onClick={handleDistanceGateClick}
                      className="inline-flex items-center gap-1 whitespace-nowrap underline decoration-dotted underline-offset-2"
                    >
                      <MapPinOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {dropoffStatus.aria}
                    </button>
                  ) : dropoffStatus.kind === 'far' ? (
                    <button
                      type="button"
                      onClick={handleRangeInfoClick}
                      className="whitespace-nowrap underline decoration-dotted underline-offset-2"
                    >
                      {dropoffStatus.aria}
                    </button>
                  ) : (
                    <span className="whitespace-nowrap">{dropoffStatus.aria}</span>
                  )}
                </>
              ) : null}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  // Rendered identically collapsed and expanded, so the two views can't drift
  // in what they say about a listing's hand-off times.
  //
  // Solid backgrounds rather than a tinted wash: these sit on top of a photo,
  // and a translucent fill left the gold drop-off pill reading gold-on-pale-
  // gold.
  //
  // Black text on both, not Ionic's paired contrast colour (which would put
  // white on the blue pickup pill). A literal colour is right here rather than
  // a theme token: `--ion-color-secondary` and `--ion-color-tertiary` are
  // defined identically in the light and dark blocks of variables.scss, so the
  // pill backgrounds never change between themes and the text on them mustn't
  // either. `--ion-text-color` would flip to white in dark mode and vanish.
  const renderWindowPills = (showTz: boolean) =>
    windowPills.map(({ method, Icon, tone, label }, i) => (
      <span
        key={method}
        className="flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
        style={{
          backgroundColor: `var(--ion-color-${tone})`,
          color: '#000',
        }}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate whitespace-nowrap">
          {/* The zone is stated once — repeating it on both pills is noise
              when they share a listing (and therefore a zone). */}
          {i === 0 && showTz ? `${label} ${tzAbbrev}` : label}
        </span>
        <span className="sr-only">{t(`orders:card.fulfillment.${method}` as const)}</span>
        {/* Drop-off range/fee rides in this same pill rather than a second one
            below it: it qualifies THIS window, and a bar separator keeps it
            visibly subordinate to the times. Terse by design — a price or a
            glyph, never a sentence — with the sentence going to `aria` so a
            screen reader still gets something meaningful. */}
        {method === 'dropoff' && dropoffStatus ? (
          <>
            <span aria-hidden className="opacity-40">|</span>
            {/* The locate case puts the same sentence on the button's
                aria-label, so emitting it here too would announce it twice. */}
            {dropoffStatus.kind !== 'locate' ? (
              <span className="sr-only">{dropoffStatus.aria}</span>
            ) : null}
            {dropoffStatus.kind === 'fee' ? (
              <span className="shrink-0 whitespace-nowrap">{dropoffStatus.fee}</span>
            ) : dropoffStatus.kind === 'locate' ? (
              <button
                type="button"
                onClick={handleDistanceGateClick}
                aria-label={dropoffStatus.aria}
                className="inline-flex shrink-0 items-center"
              >
                <MapPinOff className="h-3.5 w-3.5" aria-hidden />
              </button>
            ) : (
              // Out of range: said in words, "Too far". Tapping explains how
              // far is too far against the eater's actual distance.
              <button
                type="button"
                onClick={handleRangeInfoClick}
                aria-label={dropoffStatus.aria}
                className="inline-flex shrink-0 items-center whitespace-nowrap"
              >
                {t('card.dropoffTooFarShort')}
              </button>
            )}
          </>
        ) : null}
      </span>
    ));


  // --- Timezone label next to the time window ---
  // The abbreviation ("MDT", "EST") is computed in the LISTING's own zone for
  // the window's start instant, so DST is correct for that date. The expanded
  // card always shows it; the collapsed card only shows it when the listing's
  // zone reads a different wall-clock time than the viewer's — i.e. a different
  // UTC offset at that instant — so an equal-offset zone isn't redundant noise.
  // The browse area comes from the operating LOCALE (the TopBar pill) — that's
  // what "where the viewer is browsing" means. The TopBar ZIP switch updates
  // `locale` but deliberately leaves `location` (device/typed coords) alone, so
  // deriving the viewer zone from `location` would compare against stale coords.
  // Prefer the locale centroid, then device/typed coords, then the device zone.
  const viewerLocale = useLocationStore((s) => s.locale);
  const viewerLocation = useLocationStore((s) => s.location);
  const listingTz = timeWindow?.timezone ?? null;
  const windowStartMs = useMemo(() => {
    if (!timeWindow?.start) return null;
    const ms = zonedWallClockToInstant(timeWindow.start, listingTz).getTime();
    return Number.isNaN(ms) ? null : ms;
  }, [timeWindow?.start, listingTz]);
  const tzAbbrev = useMemo(
    () =>
      listingTz && windowStartMs != null
        ? timeZoneAbbreviation(listingTz, new Date(windowStartMs))
        : null,
    [listingTz, windowStartMs],
  );
  const viewerTz = useMemo(
    () =>
      deriveTimezone(viewerLocale?.lat, viewerLocale?.lon) ??
      deriveTimezone(viewerLocation?.lat, viewerLocation?.lon) ??
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    [viewerLocale?.lat, viewerLocale?.lon, viewerLocation?.lat, viewerLocation?.lon],
  );
  const tzDiffersFromViewer = useMemo(() => {
    if (!listingTz || windowStartMs == null) return false;
    const at = new Date(windowStartMs);
    const listingOffset = zoneOffsetMinutes(listingTz, at);
    const viewerOffset = zoneOffsetMinutes(viewerTz, at);
    if (listingOffset == null || viewerOffset == null) return false;
    return listingOffset !== viewerOffset;
  }, [listingTz, viewerTz, windowStartMs]);

  // Distance is exposed only when the user's location is device-sourced. The
  // query layer already nulls `distance` for non-device sources (belt), but we
  // also check the source directly so we can render the "no location" pin
  // (suspenders) rather than silently dropping the affordance.
  const hasDeviceLocation = useLocationStore((s) => s.locationSource === 'device');

  const handleDistanceGateClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDistanceGateClick?.();
  };

  // Same stop-the-card-navigating dance: these controls live inside the card's
  // clickable surface, so without this a tap opens the listing instead.
  const handleRangeInfoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setRangeInfoOpen(true);
  };

  // Shared between the collapsed overlay and the expanded meta row. Inherits
  // text color from its parent (black on the image overlay, muted when
  // expanded). Renders nothing outside browse contexts (showDistanceGate off).
  const distanceNode =
    hasDeviceLocation && distance != null ? (
      <span className="shrink-0">{t('card.distanceMi', { miles: distance.toFixed(1) })}</span>
    ) : showDistanceGate ? (
      <button
        type="button"
        onClick={handleDistanceGateClick}
        aria-label={t('card.distanceUnavailableAria')}
        className="inline-flex shrink-0 items-center"
      >
        <MapPinOff className="h-4 w-4" aria-hidden="true" />
      </button>
    ) : null;



  const activeProfileId = useProfileStore((s) => s.activeProfile?.id);
  const isOwner = Boolean(
    activeProfileId && listerSummary?.id && activeProfileId === listerSummary.id,
  );
  const showOwnerFraction = isOwner && quantity != null && itemsRemaining != null;

  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  // Owned per-card rather than hoisted to the page like the distance gate:
  // that one was hoisted because useAddressCapture instantiates mutations, and
  // a long feed shouldn't pay for one per card. This is static text, and Modal
  // renders null while closed, so a local instance costs nothing.
  const [rangeInfoOpen, setRangeInfoOpen] = useState(false);

  const isUserPhoto = Boolean(imageUrl && !imageUrl.includes('/placeholder-image/'));

  const bottomTextRef = useRef<HTMLDivElement | null>(null);
  const [bottomGradientHeight, setBottomGradientHeight] = useState(80);

  useEffect(() => {
    if (isExpanded) return;
    const el = bottomTextRef.current;
    if (!el) return;
    const measure = () => setBottomGradientHeight(el.offsetHeight + 10);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isExpanded]);

  // Sold out has two routes and the card names them apart: the lister stopped
  // taking orders early (`orders_closed`) vs the portions simply running out
  // (`sold_out`). Retired listings (canceled/expired) keep the generic
  // "Unavailable". The rule itself lives in getListingAvailability so this card
  // and the detail page's order CTA can't answer it differently.
  const { unavailable: isUnavailable, reason: unavailableReason, soldOut } =
    getListingAvailability(listing);
  const unavailableLabel =
    unavailableReason === 'orders_closed'
      ? t('card.ordersClosed')
      : unavailableReason === 'sold_out'
        ? t('card.soldOut')
        : t('card.unavailable');

  // `soldOut` is true for BOTH sold-out routes and false for retired listings,
  // which is exactly the gate we want — so the stamp's wording still comes from
  // `unavailableLabel` and names which route it was.
  const showStamp = showSoldOutStamp && soldOut;

  const lowStock =
    quantity != null && quantity > 0 && itemsRemaining != null && itemsRemaining / quantity < 0.25;

  const hasExpandableContent = Boolean(
    description ||
      (originTags && originTags.length > 0) ||
      (dietaryTags && dietaryTags.length > 0) ||
      (allergenTags && allergenTags.length > 0),
  );

  const showChevron = !disableExpand && hasExpandableContent;

  const wrapperClass = [
    'group block w-full max-w-[360px] mx-auto text-left',
    // The stamp's scrim already reads as "not available", and stacking the
    // 60% fade on top of it washes the watermark out. Retired listings (no stamp)
    // keep the fade.
    isUnavailable && !showStamp ? 'opacity-60' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  // The clickable card is a div with role="button", not a real <button>: it
  // contains its own controls (the distance-gate pin, the chevron toggle) and
  // a <button> inside a <button> is invalid HTML — React warns on every feed
  // render and browsers/AT disagree on how to treat the inner control. The
  // role + tabIndex + key handler below restore what the native element gave
  // keyboard users.
  const handleWrapperKeyDown = (e: React.KeyboardEvent) => {
    // Only when the card ITSELF has focus. Keystrokes bubbling up from the
    // inner buttons are theirs to handle — otherwise Enter on the chevron
    // would both toggle and open the listing.
    if (e.target !== e.currentTarget) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault(); // Space would otherwise scroll the feed.
    onClick?.();
  };

  return (
    <div
      data-testid="dish-info-card"
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? handleWrapperKeyDown : undefined}
      className={wrapperClass}
      style={
        status === 'expired' || status === 'canceled' ? { filter: 'grayscale(100%)' } : undefined
      }
    >
      <div
        className={`relative overflow-hidden rounded-2xl aspect-[4/3] ${
          variant === 'floating'
            ? 'shadow-[0_4px_12px_rgba(0,0,0,0.5)]'
            : 'ring-1 ring-border shadow-sm'
        }`}
        style={{ backgroundColor: 'var(--ion-color-light)' }}
      >
        {thumbnailUrl || imageUrl ? (
          <img
            src={thumbnailUrl ?? imageUrl ?? ''}
            alt={title}
            className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
            {t('card.noImage')}
          </div>
        )}

        {/* Image overlays — only when collapsed */}
        {!isExpanded ? (
          <>
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0"
              style={{
                height: bottomGradientHeight,
                background:
                  status === 'draft'
                    ? 'linear-gradient(to top, var(--ion-color-warning-shade) 0%, color-mix(in srgb, var(--ion-color-warning-shade) 75%, transparent) 90%, transparent 100%)'
                    : 'linear-gradient(to top, var(--ion-color-light-shade) 0%, color-mix(in srgb, var(--ion-color-light-shade) 75%, transparent) 90%, transparent 100%)',
              }}
            />

            {isUserPhoto ? (
              <div
                className="pointer-events-none absolute inset-x-0 top-0 h-10"
                style={{
                  background:
                    'linear-gradient(to bottom, var(--ion-color-light-shade) 0%, color-mix(in srgb, var(--ion-color-light-shade) 75%, transparent) 60%, transparent 100%)',
                }}
              />
            ) : null}

            {/* Top-left: time window (or "Draft" badge for unpublished listings).
                `whitespace-nowrap` keeps it on a single line so large text can't
                grow it downward into the title block below. */}
            {status === 'draft' || windowPills.length > 0 || availabilityLabel ? (
              <div className="absolute top-2 left-2 flex max-w-[75%] flex-col items-start gap-1 text-xs">
                {status === 'draft' ? (
                  <span className="block truncate whitespace-nowrap text-black">
                    {t('card.draftBadge')}
                  </span>
                ) : windowPills.length > 0 ? (
                  // Each window carries its own method icon and colour, matching
                  // the badges on the publish form so a chef recognises them.
                  renderWindowPills(!!tzAbbrev && tzDiffersFromViewer)
                ) : (
                  <span className="block truncate whitespace-nowrap text-black">
                    {tzAbbrev && tzDiffersFromViewer
                      ? `${availabilityLabel} ${tzAbbrev}`
                      : availabilityLabel}
                  </span>
                )}
              </div>
            ) : null}

            {/* Top-right: distance (or "no location" pin) + fulfillment icons */}
            {distanceNode || (windowPills.length === 0 && fulfillmentIcons.length > 0) ? (
              <div className="absolute top-2 right-2 flex items-center gap-1.5 text-xs text-black">
                {distanceNode}
                {/* Only for the legacy single-window shape. When the per-method
                    pills render, each already carries its own icon and these
                    would just repeat them. */}
                {windowPills.length === 0 &&
                  fulfillmentIcons.map(({ method, label, Icon }) => (
                    <Icon key={method} className="h-4 w-4" aria-label={label} />
                  ))}
              </div>
            ) : null}

            {/* Bottom-left: title + attribution + distance.
                `pr-20` reserves room on the right for the absolutely-positioned
                price/portions block so the (clamped) title can't slide under it.
                `max-h-[72%]` caps this block to the lower portion of the card so
                that on short cards (e.g. the narrow activity-feed render) a large
                two-line title can't grow up into the top time row. */}
            <div ref={bottomTextRef} className="absolute inset-x-0 bottom-0 pl-4 pr-20 pb-3 text-black max-h-[72%] overflow-hidden">
              <h2 className="font-normal text-lg leading-tight line-clamp-2 break-words">
                {title}
              </h2>
              <UserAttribution
                summary={listerSummary}
                prefix={t('card.by')}
                fallbackName={t('card.unknownChef')}
                className="mt-1.5 text-xs text-black/85"
                interactive
              />
            </div>

            {/* Bottom-right: price + portions */}
            <div className="absolute bottom-2 right-3 flex flex-col items-end text-black">
              {price != null ? (
                <span className="text-sm font-normal leading-tight">
                  {formatPriceCents(price, { dropZeroCents: true })}
                </span>
              ) : null}
              {showOwnerFraction ? (
                <span
                  className={[
                    'text-[11px] font-medium leading-tight',
                    lowStock ? 'text-brand-danger' : 'text-black/80',
                  ].join(' ')}
                >
                  {t('card.itemsFraction', { remaining: itemsRemaining, total: quantity })}
                </span>
              ) : !isUnavailable && itemsRemaining != null && itemsRemaining > 0 ? (
                <span
                  className={[
                    'text-[11px] font-medium leading-tight',
                    lowStock ? 'text-brand-danger' : 'text-black/80',
                  ].join(' ')}
                >
                  {t('card.itemsLeft', { count: itemsRemaining })}
                </span>
              ) : isUnavailable && !showStamp ? (
                // Suppressed when the stamp is up: it already says this, far
                // more loudly, a few pixels away.
                <span className="text-[11px] font-medium leading-tight">
                  {unavailableLabel}
                </span>
              ) : null}
            </div>
          </>
        ) : null}

        {/* Sold-out stamp. Sits outside the `!isExpanded` block on purpose:
            being sold out is a fact about the listing, not a collapsed-card
            overlay, so it stays put when the user expands the description.
            Rendered after the gradients so it paints over them.

            `pointer-events-none` so the scrim can't swallow taps meant for the
            card, or for the distance-gate pin in the overlay beneath it (the
            chevron is a sibling of the image container, so it's unaffected).
            No `aria-hidden` — with the stamp up we suppress the small corner
            label below, so this is the only place the status is stated.

            Styled as a watermark rather than an alert: a light scrim and
            large, translucent, slightly rotated white type. Sold out isn't an
            error, so it shouldn't shout in danger red. The scrim and text are
            fixed rgba rather than theme tokens because they sit on a PHOTO,
            not a themed surface, so they need to read the same in both
            themes. */}
        {showStamp ? (
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.25)' }}
          >
            <span
              className="-rotate-12 select-none text-3xl font-extrabold uppercase leading-none tracking-[0.2em]"
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.35)',
              }}
            >
              {unavailableLabel}
            </span>
          </div>
        ) : null}
      </div>

      {/* Out-of-range explainer. A glyph can't say how far is too far, so this
          states the chef's radius against the eater's actual distance. Mounted
          here (inside the card) rather than hoisted to the page because the
          numbers are per-listing and Modal is free while closed. */}
      {dropoffStatus?.kind === 'far' ? (
        <Modal
          isOpen={rangeInfoOpen}
          onClose={() => setRangeInfoOpen(false)}
          size="sm"
          className="space-y-4"
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-100 text-muted-foreground">
              <Icons.dropoff className="h-6 w-6" aria-hidden />
            </div>
            <h3 className="text-base font-medium text-foreground">
              {t('card.dropoffRangeTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('card.dropoffRangeBody', {
                radius: dropoffRadius,
                distance: distance?.toFixed(1),
              })}
            </p>
            {/* Only worth saying when pickup is genuinely on the table — on a
                drop-off-only listing it would be a dead end. */}
            {fulfillmentMethod?.includes('pickup') ? (
              <p className="text-sm text-muted-foreground">
                {t('card.dropoffRangePickupHint')}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setRangeInfoOpen(false)}
            className="w-full rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white"
          >
            {t('card.dropoffRangeDismiss')}
          </button>
        </Modal>
      ) : null}

      {/* Title / attribution / price block — shown below image when expanded */}
      {isExpanded ? (
        <div className="mt-3 px-1">
          <h2 className="font-normal text-lg leading-tight text-foreground">
            {title}
          </h2>
          {/* Distance, and the legacy single-window label for listings with no
              method windows. The per-method rows moved BELOW the price block —
              see the note down there. */}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {windowPills.length === 0 ? (
              <>
                {availabilityLabel ? (
                  <span>{tzAbbrev ? `${availabilityLabel} ${tzAbbrev}` : availabilityLabel}</span>
                ) : null}
                {fulfillmentIcons.length > 0 ? (
                  <div className="flex items-center gap-1">
                    {fulfillmentIcons.map(({ method, label, Icon }) => (
                      <Icon key={method} className="h-3.5 w-3.5" aria-label={label} />
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}
            {distanceNode}
          </div>
          <div className="mt-1 flex items-center justify-between gap-2">
            <UserAttribution
              summary={listerSummary}
              prefix={t('card.by')}
              fallbackName={t('card.unknownChef')}
              className="text-xs text-muted-foreground"
              interactive
            />
            <div className="flex flex-col items-end shrink-0">
              {price != null ? (
                <span className="text-sm font-normal text-foreground leading-tight">
                  {formatPriceCents(price, { dropZeroCents: true })}
                </span>
              ) : null}
              {showOwnerFraction ? (
                <span
                  className={[
                    'text-[11px] font-medium leading-tight',
                    lowStock ? 'text-brand-danger' : 'text-muted-foreground',
                  ].join(' ')}
                >
                  {t('card.itemsFraction', { remaining: itemsRemaining, total: quantity })}
                </span>
              ) : !isUnavailable && itemsRemaining != null && itemsRemaining > 0 ? (
                <span
                  className={[
                    'text-[11px] font-medium leading-tight',
                    lowStock ? 'text-brand-danger' : 'text-muted-foreground',
                  ].join(' ')}
                >
                  {t('card.itemsLeft', { count: itemsRemaining })}
                </span>
              ) : isUnavailable && !showStamp ? (
                // Same suppression as the collapsed block above — the stamp
                // stays visible over the photo when the card is expanded.
                <span className="text-[11px] font-medium leading-tight text-brand-medium">
                  {unavailableLabel}
                </span>
              ) : null}
            </div>
          </div>

          {/* Hand-off rows sit BELOW the name / chef / price block. Above it
              they separated the title from its own attribution and price, and
              being the tallest element they pushed the thing the eater is
              actually deciding on down the card. */}
          {windowPills.length > 0 || dropoffClosed ? (
            <div className="mt-3 flex flex-col gap-3 text-sm">
              {windowPills.length > 0 ? renderWindowRows() : null}
              {dropoffClosed ? (
                // Muted version of a drop-off row: same icon and layout, so it
                // reads as "that option, now closed" rather than a new notice.
                <div className="flex items-start gap-2 opacity-60">
                  <span
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted"
                    aria-hidden
                  >
                    <Icons.dropoff className="h-3.5 w-3.5" />
                  </span>
                  <div className="flex min-w-0 flex-col">
                    <span className="font-medium text-foreground">
                      {t('orders:card.fulfillment.dropoff')}
                    </span>
                    <span className="text-muted-foreground">{t('card.dropoffClosed')}</span>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Chevron toggle row */}
      {showChevron ? (
        <div className="mt-1 flex justify-end">
          <button
            type="button"
            onClick={handleToggle}
            aria-label={isExpanded ? t('card.collapseDetails') : t('card.expandDetails')}
            aria-expanded={isExpanded}
            className="rounded-full p-1 text-muted-foreground hover:text-foreground hover:bg-surface-100 transition-colors"
          >
            <svg
              className={[
                'h-4 w-4 transition-transform',
                isExpanded ? 'rotate-180' : '',
              ].join(' ')}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      ) : null}

      {/* Expanded panel */}
      {showChevron && isExpanded ? (
        <div className="mt-2 space-y-3 px-1">
          {description ? (
            <p className="text-sm leading-relaxed text-muted-foreground">
              {description}
            </p>
          ) : null}

          {(originTags && originTags.length > 0) ||
          (dietaryTags && dietaryTags.length > 0) ? (
            <div className="flex flex-wrap gap-1.5">
              {originTags?.map((tag) => (
                <span
                  key={`origin-${tag}`}
                  className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-xs text-foreground"
                >
                  {formatTag(tag)}
                </span>
              ))}
              {dietaryTags?.map((tag) => (
                <span
                  key={`diet-${tag}`}
                  className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-xs text-foreground"
                >
                  {formatTag(tag)}
                </span>
              ))}
            </div>
          ) : null}

          {allergenTags && allergenTags.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-foreground/80">
                {t('card.mayContain')}
              </span>
              {allergenTags.map((tag) => (
                <span
                  key={`allergen-${tag}`}
                  className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-xs text-foreground"
                >
                  {formatTag(tag)}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
